import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useContent, useLang, NAV_LINKS, loc, UI } from "@/lib/content";
import { scrollToId } from "@/lib/api";
import { LogoLockup } from "./LogoMark";

export default function Nav() {
  const { content } = useContent();
  const { lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToId(href);
  };

  const LangToggle = ({ className = "" }) => (
    <div className={`inline-flex items-center rounded-full border border-line bg-white/70 p-1 font-mono text-[11px] font-semibold ${className}`}>
      {["es", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-toggle-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 uppercase transition-all ${lang === l ? "bg-navy text-paper" : "text-mutedtext hover:text-navy"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="nav-bar"
      className={`fixed inset-x-0 top-0 z-[70] transition-all duration-300 ${
        scrolled || open ? "bg-paper/90 backdrop-blur-xl border-b border-line/70 shadow-sm" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 sm:px-8">
        <a href="#top" data-testid="nav-home-link" onClick={(e) => go(e, "#top")} aria-label={content.brand_name}>
          <LogoLockup brand={content.brand_name} suffix={loc(content.brand_suffix, lang)} />
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              onClick={(e) => go(e, l.href)}
              className="text-[13px] font-semibold text-navy/70 transition-colors hover:text-heritage"
            >
              {loc(l.label, lang)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:inline-flex" />
          <a
            href="#inscripcion"
            data-testid="nav-cta-button"
            onClick={(e) => go(e, "#inscripcion")}
            className="hidden rounded-full bg-heritage px-5 py-2.5 text-[13px] font-bold text-paper shadow-md shadow-heritage/25 transition-all hover:-translate-y-0.5 hover:bg-heritagedark sm:inline-flex"
          >
            {loc(UI.navCta, lang)}
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-line bg-white/70 text-navy lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Abrir menú"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-line/60 bg-paper/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  className="rounded-lg px-3 py-2.5 text-sm font-semibold text-navy/80 hover:bg-paperdeep"
                >
                  {loc(l.label, lang)}
                </a>
              ))}
              <div className="mt-2 flex items-center gap-3">
                <a
                  href="#inscripcion"
                  onClick={(e) => go(e, "#inscripcion")}
                  className="flex-1 rounded-full bg-heritage px-5 py-3 text-center text-sm font-bold text-paper"
                >
                  {loc(UI.navCta, lang)}
                </a>
                <LangToggle />
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
